import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Mail, Phone, Printer } from "lucide-react";
import { site } from "@/config/site";
import { SEO } from "@/components/site/SEO";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function QuotePage() {
  const { id, ref, token } = useParams();
  const [quote, setQuote] = useState(null);
  const [missing, setMissing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");
  const [panel, setPanel] = useState(""); // "" | "changes" | "decline"
  const [feedback, setFeedback] = useState("");
  const [fbBusy, setFbBusy] = useState(false);
  const [fbError, setFbError] = useState("");

  const base = `${process.env.REACT_APP_BACKEND_URL}/api/quotes`;
  const viewUrl = token ? `${base}/${ref}/${token}` : `${base}/${id}`;
  const acceptUrl = token ? `${base}/${ref}/${token}/accept` : `${base}/${id}/accept`;

  useEffect(() => {
    axios.get(viewUrl).then((r) => setQuote(r.data)).catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ref, token]);

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    setAcceptError("");
    try {
      await axios.post(acceptUrl);
      setQuote((q) => ({ ...q, status: "Accepted" }));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "quote_accepted", quote_ref: quote?.ref });
    } catch {
      setAcceptError("Something went wrong — please call us and we'll confirm over the phone.");
    } finally {
      setAccepting(false);
    }
  };

  const respond = async (kind) => {
    if (fbBusy) return;
    if (kind === "changes" && !feedback.trim()) {
      setFbError("Please tell us what you'd like changed.");
      return;
    }
    setFbBusy(true);
    setFbError("");
    const slug = kind === "changes" ? "request-changes" : "decline";
    try {
      await axios.post(`${viewUrl}/${slug}`, { message: feedback });
      setQuote((q) => ({ ...q, status: kind === "decline" ? "Declined" : "Changes Requested" }));
      setPanel("");
    } catch (e) {
      setFbError(e.response?.data?.detail || "Something went wrong — please call us instead.");
    } finally {
      setFbBusy(false);
    }
  };

  const isAccepted = quote?.status === "Accepted";

  return (
    <div className="min-h-screen bg-warm-50 grain relative py-10 sm:py-16 px-4 print:bg-white print:py-0">
      <SEO title="Your Quotation | My Blinds Company" description="Your made-to-measure quotation from My Blinds Company." path={`/quote/${ref || id}`} />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src="/logo.png" alt="My Blinds Company logo" className="h-12 w-auto rounded" />
        </div>
        {missing ? (
          <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center shadow-sm" data-testid="quote-missing">
            <h1 className="font-serif text-2xl text-slate-900 mb-2">Quotation not found</h1>
            <p className="text-sm text-slate-600">
              This link may have expired or been mistyped. Call us on {site.phone.display} and we'll help.
            </p>
          </div>
        ) : !quote ? (
          <p className="text-center text-sm text-slate-500">Loading your quotation…</p>
        ) : (
          <div className="rounded-[1.5rem] border border-warm-200 bg-white shadow-xl overflow-hidden print:shadow-none print:border-0" data-testid="quote-card">
            <div className="bg-navy-950 text-white p-8 print:bg-white print:text-slate-900">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1 print:text-slate-500">Quotation</p>
              <h1 className="font-serif text-3xl">{quote.ref}</h1>
              <p className="text-sm text-white/60 mt-2 print:text-slate-500">
                Prepared for {quote.customerFirstName || "you"} ·{" "}
                {new Date(quote.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                {" · valid for 30 days"}
              </p>
            </div>
            <div className="p-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-warm-200">
                    <th className="pb-3 font-semibold">Item</th>
                    <th className="pb-3 font-semibold text-center">Qty</th>
                    <th className="pb-3 font-semibold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((i, idx) => (
                    <tr key={idx} className="border-b border-warm-200/70">
                      <td className="py-3.5 text-slate-800">{i.description}</td>
                      <td className="py-3.5 text-center text-slate-600">{i.qty}</td>
                      <td className="py-3.5 text-right text-slate-800">{gbp(i.price * i.qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-5 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span><span>{gbp(quote.subtotal)}</span>
                </div>
                {quote.discount > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Discount</span><span>−{gbp(quote.discount)}</span>
                  </div>
                )}
                {quote.vat && (
                  <div className="flex justify-between text-slate-600">
                    <span>VAT (20%)</span><span>{gbp(quote.vatAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold text-slate-900 border-t border-warm-200 pt-3">
                  <span>Total</span><span data-testid="quote-total">{gbp(quote.total)}</span>
                </div>
                {quote.deposit_required > 0 && (
                  <>
                    <div className="flex justify-between text-slate-600 pt-1">
                      <span>Deposit to confirm</span><span>{gbp(quote.deposit_required)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Balance on completion</span><span>{gbp(quote.balance_due)}</span>
                    </div>
                  </>
                )}
              </div>
              {quote.notes && (
                <p className="mt-5 rounded-xl bg-warm-100 p-4 text-sm text-slate-700">{quote.notes}</p>
              )}
              {quote.terms && (
                <div className="mt-6">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Terms &amp; conditions</h2>
                  <p className="text-[11px] leading-relaxed text-slate-500 whitespace-pre-line">{quote.terms}</p>
                </div>
              )}
              <p className="mt-5 text-xs leading-relaxed text-slate-500">
                Everything is made to measure and professionally fitted by our own team. This
                quotation is free and without obligation.
              </p>

              {isAccepted ? (
                <div className="mt-7 rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="quote-accepted-banner">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                  <p className="font-serif text-xl text-slate-900">Quotation accepted — thank you</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                    We've been notified and will be in touch shortly to arrange your deposit and
                    confirm the next steps.
                  </p>
                </div>
              ) : quote.status === "Declined" ? (
                <div className="mt-7 rounded-2xl bg-slate-50 border border-slate-200 p-6 text-center" data-testid="quote-declined-banner">
                  <p className="font-serif text-xl text-slate-900">Quotation declined</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                    Thanks for letting us know. If anything changes, we're only a phone call away
                    — {site.phone.display}.
                  </p>
                </div>
              ) : quote.status === "Changes Requested" ? (
                <div className="mt-7 rounded-2xl bg-amber-50 border border-amber-200 p-6 text-center" data-testid="quote-changes-banner">
                  <p className="font-serif text-xl text-slate-900">Changes requested</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                    Thank you — we've received your feedback and we'll come back to you shortly
                    with an updated quotation.
                  </p>
                </div>
              ) : (
                <div className="mt-7 space-y-3 no-print">
                  <button
                    type="button"
                    data-testid="quote-accept-btn"
                    onClick={accept}
                    disabled={accepting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 py-4 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition-all duration-300 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {accepting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Accept This Quotation
                  </button>
                  {acceptError && <p role="alert" data-testid="quote-accept-error" className="text-xs text-red-700 text-center">{acceptError}</p>}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      data-testid="quote-changes-btn"
                      onClick={() => { setPanel(panel === "changes" ? "" : "changes"); setFbError(""); }}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-navy-900/25 py-3 text-sm font-semibold text-navy-900 hover:bg-navy-900 hover:text-white transition-colors"
                    >
                      Request changes
                    </button>
                    <button
                      type="button"
                      data-testid="quote-decline-btn"
                      onClick={() => { setPanel(panel === "decline" ? "" : "decline"); setFbError(""); }}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-warm-300 py-3 text-sm font-semibold text-slate-500 hover:bg-warm-100 transition-colors"
                    >
                      Decline quote
                    </button>
                  </div>
                  {panel && (
                    <div className="rounded-2xl border border-warm-200 bg-warm-100/60 p-4 space-y-3" data-testid={`quote-${panel}-panel`}>
                      <textarea
                        rows={3}
                        data-testid="quote-feedback-text"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={panel === "changes" ? "Tell us what you'd like changed — a different fabric or colour, the price, the items…" : "Optional — let us know why, it helps us improve"}
                        className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3 text-sm focus:border-navy-900 focus:outline-none resize-none"
                      />
                      {fbError && <p role="alert" data-testid="quote-feedback-error" className="text-xs text-red-700">{fbError}</p>}
                      <button
                        type="button"
                        data-testid="quote-feedback-submit"
                        onClick={() => respond(panel)}
                        disabled={fbBusy}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 py-3 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50 transition-colors"
                      >
                        {fbBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                        {panel === "changes" ? "Send change request" : "Decline quotation"}
                      </button>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={site.phone.href}
                      data-testid="quote-call-btn"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 py-3.5 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
                    >
                      <Phone className="h-4 w-4" /> Call us — {site.phone.display}
                    </a>
                    <a
                      href={`mailto:${site.email}?subject=Quotation ${quote.ref}`}
                      data-testid="quote-email-btn"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-navy-900/25 py-3.5 text-sm font-semibold text-navy-900 hover:bg-navy-900 hover:text-white transition-colors"
                    >
                      <Mail className="h-4 w-4" /> Reply by email
                    </a>
                  </div>
                  <button
                    type="button"
                    data-testid="quote-print-btn"
                    onClick={() => window.print()}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-warm-300 py-3 text-xs font-semibold text-slate-600 hover:bg-warm-100 transition-colors"
                  >
                    <Printer className="h-4 w-4" /> Print / Save as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-slate-400 mt-8">
          {site.legalName} · Unit 13d, 47 Whitmore Road, Small Heath, Birmingham, B10 0NR
        </p>
      </div>
    </div>
  );
}
